import { ref } from 'vue'
import { getStorage, setStorage } from '@/utils/storage'
import { STORAGE_KEYS } from '@/constants/storageKeys'
import { updateTelegramHeaderColor } from '@/utils/telegramViewport'
import { syncTelegramThemeCssVars } from '@/utils/telegramTheme'

const THEME_COLOR_LIGHT = '#f8f7fb'
const THEME_COLOR_DARK = '#121218'
const DARK_LUMINANCE_THRESHOLD = 0.45
let telegramThemeListenerBound = false

export const THEME_MODES = Object.freeze({
  SYSTEM: 'system',
  LIGHT: 'light',
  DARK: 'dark',
})

function readThemeMode() {
  const stored = getStorage(STORAGE_KEYS.THEME_MODE)
  if (Object.values(THEME_MODES).includes(stored)) return stored
  return THEME_MODES.SYSTEM
}

export const themeMode = ref(readThemeMode())

function getSystemPrefersDark() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function getTelegramColorScheme() {
  const tg = window.Telegram?.WebApp
  const scheme = tg?.colorScheme
  if (scheme === 'dark' || scheme === 'light') return scheme

  const bgColor = tg?.themeParams?.bg_color || tg?.themeParams?.secondary_bg_color
  if (typeof bgColor === 'string') {
    const normalized = bgColor.trim().replace(/^#/, '')
    const hex = normalized.length === 3
      ? normalized.split('').map((char) => char + char).join('')
      : normalized
    if (/^[0-9a-fA-F]{6}$/.test(hex)) {
      const red = parseInt(hex.slice(0, 2), 16) / 255
      const green = parseInt(hex.slice(2, 4), 16) / 255
      const blue = parseInt(hex.slice(4, 6), 16) / 255
      const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue
      return luminance < DARK_LUMINANCE_THRESHOLD ? 'dark' : 'light'
    }
  }

  return null
}

function bindTelegramThemeListener() {
  const tg = window.Telegram?.WebApp
  if (!tg?.onEvent || telegramThemeListenerBound) return

  tg.onEvent('themeChanged', () => {
    syncTelegramThemeCssVars({ isDark: resolveIsDark() })
    if (themeMode.value === THEME_MODES.SYSTEM) {
      syncTheme()
      return
    }
    updateTelegramHeaderColor()
  })
  telegramThemeListenerBound = true
}

export function resolveIsDark(mode = themeMode.value) {
  if (mode === THEME_MODES.LIGHT) return false
  if (mode === THEME_MODES.DARK) return true

  const tgScheme = getTelegramColorScheme()
  if (tgScheme) return tgScheme === 'dark'
  return getSystemPrefersDark()
}

export function applyTheme(isDark) {
  const root = document.documentElement
  root.classList.toggle('dark', isDark)
  root.style.colorScheme = isDark ? 'dark' : 'light'

  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    meta.setAttribute('content', isDark ? THEME_COLOR_DARK : THEME_COLOR_LIGHT)
  }
}

export function syncTheme() {
  bindTelegramThemeListener()
  const isDark = resolveIsDark()
  applyTheme(isDark)
  syncTelegramThemeCssVars({ isDark })
  updateTelegramHeaderColor()
}

export function setThemeMode(mode) {
  if (!Object.values(THEME_MODES).includes(mode)) return
  themeMode.value = mode
  setStorage(STORAGE_KEYS.THEME_MODE, mode)
  syncTheme()
}

export function initTheme() {
  themeMode.value = readThemeMode()
  syncTheme()

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  const onSystemChange = () => {
    if (themeMode.value === THEME_MODES.SYSTEM) {
      syncTheme()
    }
  }
  mediaQuery.addEventListener('change', onSystemChange)

  bindTelegramThemeListener()

  return () => {
    mediaQuery.removeEventListener('change', onSystemChange)
  }
}
